import React from 'react'
import { MapPin } from 'lucide-react'
import { Tooltip } from '../../ui'

// Same categorical slots, in the same order, as the library donuts.
const SLOTS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300']
const OTHER_COLOR = '#b4b2a9'

interface Props {
  title: string
  /** Amount per location, keyed by zone code. Already in the unit `format` prints. */
  amounts: Map<string, number>
  labelOf: (code: string) => string
  format: (n: number) => string
}

/**
 * A split by location in venue, drawn as one segmented line with a legend.
 *
 * Presentation only: callers decide what is summed and how it is priced. The
 * per-broadcast brand page and the season-long sponsor page both draw this,
 * so "where the value was earned" looks the same at either scale.
 *
 * Six segments at most; the tail folds into Other so a brand seen in a dozen
 * places still reads as a line rather than a barcode.
 */
const LocationSplitBar: React.FC<Props> = ({ title, amounts, labelOf, format }) => {
  const ranked = [...amounts.entries()].filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1])
  if (ranked.length === 0) return null

  const head = ranked.length > SLOTS.length ? ranked.slice(0, SLOTS.length - 1) : ranked
  const tail = ranked.slice(head.length)
  const segments = head.map(([code, n], i) => ({ key: code, label: labelOf(code), n, color: SLOTS[i] }))
  if (tail.length > 0) {
    segments.push({
      key: '__other',
      label: `Other (${tail.length})`,
      n: tail.reduce((s, [, n]) => s + n, 0),
      color: OTHER_COLOR
    })
  }

  const total = segments.reduce((s, x) => s + x.n, 0)
  const pct = (n: number) => `${Math.round((n / total) * 100)}%`

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 mb-5">
      <div className="flex items-center justify-between mb-2">
        <h3 className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
          <MapPin className="h-3.5 w-3.5 text-gray-400" />{title}
        </h3>
        <span className="text-xs font-semibold text-gray-900 tabular-nums">{format(total)}</span>
      </div>

      <div className="flex h-2.5 gap-[2px]" role="img"
           aria-label={segments.map((s) => `${s.label}: ${format(s.n)}`).join(', ')}>
        {/* Sizing lives on the outer span: Tooltip wraps its child in a div,
            so flex-grow on the coloured span itself would do nothing. */}
        {segments.map((s) => (
          <span key={s.key} className="block h-full overflow-hidden rounded-sm first:rounded-l-full last:rounded-r-full"
                style={{ flexGrow: s.n, flexBasis: 0, minWidth: 4 }}>
            <Tooltip content={`${s.label}: ${format(s.n)} (${pct(s.n)})`} placement="top"
                     wrapperClassName="block h-full w-full">
              <span className="block h-full w-full cursor-default" style={{ backgroundColor: s.color }} />
            </Tooltip>
          </span>
        ))}
      </div>

      <ul className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5">
        {segments.map((s) => (
          <li key={s.key} className="inline-flex items-center gap-1.5 text-xs">
            <span className="h-2 w-2 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-gray-700">{s.label}</span>
            <span className="font-medium text-gray-900 tabular-nums">{format(s.n)}</span>
            <span className="text-gray-400 tabular-nums">{pct(s.n)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default LocationSplitBar
